import { User } from "firebase/auth";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../ConfirmModal";
import ConfirmDestructive from "../shared/ConfirmDestructive";
import {
  renameCompetition, startCompetition, archiveCompetition, unarchiveCompetition, deleteCompetition,
  AdminCompetition,
  fetchCompetitionGrants, createCompetitionGrant, deleteCompetitionGrant, CompetitionGrant,
} from "../../../api/admin";
import { competitionModeLabel } from "../../../lib/admin/competitionModes";
import { card, primaryBtn, secondaryBtn, dangerBtn, smallBtn, inputStyle, sectionLabel, panelHeading, errorStyle } from "../../../styles";
import { useIsMobile } from "../../../hooks/useIsMobile";

interface Props {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
}

function advisoryWarnings(comp: AdminCompetition, t: (key: string, opts?: Record<string, unknown>) => string): string[] {
  const warnings: string[] = [];
  if (comp.clubs.length === 0) warnings.push(t("admin.overview.warnings.noClubs"));
  if (comp.supportedModes.length === 0) warnings.push(t("admin.overview.warnings.noModes"));
  if (comp.supportedModes.includes("FINAL_POSITIONS") && (comp.config?.topTierSize == null || comp.config?.relegationSize == null)) {
    warnings.push(t("admin.overview.warnings.fpNotConfigured"));
  }
  if ((comp.supportedModes.includes("TARGET") || comp.supportedModes.includes("GAMEWEEK_CHALLENGE")) && !comp.hasActiveSquadPlayers) {
    warnings.push(t("admin.overview.warnings.noActivePlayers"));
  }
  if (comp.supportedModes.includes("SCORE_PREDICTOR") && !comp.hasFixtures) {
    warnings.push(t("admin.overview.warnings.noFixtures"));
  }
  if (comp.supportedModes.includes("SYNDICATE") && comp.syndicatePotCount === 0) {
    warnings.push(t("admin.overview.warnings.noPots"));
  }
  return warnings;
}

export default function OverviewTab({ user, comp, reload }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [nameDraft, setNameDraft] = useState(comp.name);
  const [savingName, setSavingName] = useState(false);

  const [confirmStart, setConfirmStart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteWithLeagues, setDeleteWithLeagues] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [error, setError] = useState("");

  const [grants, setGrants] = useState<CompetitionGrant[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [addingGrant, setAddingGrant] = useState(false);
  const [addError, setAddError] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<CompetitionGrant | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    setGrantsLoading(true);
    fetchCompetitionGrants(user, comp.id)
      .then(setGrants)
      .catch(() => {})
      .finally(() => setGrantsLoading(false));
  }, [user, comp.id]);

  const handleAddGrant = async () => {
    if (!addEmail.trim()) return;
    setAddingGrant(true);
    setAddError("");
    try {
      const grant = await createCompetitionGrant(user, comp.id, addEmail.trim());
      setGrants((prev) => [...prev, grant]);
      setAddEmail("");
    } catch (err: any) {
      setAddError(err?.message ?? t("admin.overview.access.errors.addFailed"));
    } finally {
      setAddingGrant(false);
    }
  };

  const handleRevokeGrant = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await deleteCompetitionGrant(user, comp.id, revokeTarget.id);
      setGrants((prev) => prev.filter((g) => g.id !== revokeTarget.id));
      setRevokeTarget(null);
    } catch {
      setAddError(t("admin.overview.access.errors.revokeFailed"));
    } finally {
      setRevoking(false);
    }
  };

  const handleSaveName = async () => {
    if (!nameDraft.trim() || nameDraft.trim() === comp.name) return;
    setSavingName(true);
    setError("");
    try {
      await renameCompetition(user, comp.id, nameDraft.trim(), comp.supportedModes, comp.modeJoinCaps);
      await reload();
    } catch {
      setError(t("admin.errors.failedToSave"));
    } finally {
      setSavingName(false);
    }
  };

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      await startCompetition(user, comp.id);
      setConfirmStart(false);
      await reload();
    } catch {
      setError(t("admin.overview.errors.startFailed"));
    } finally {
      setStarting(false);
    }
  };

  const handleArchiveToggle = async () => {
    setArchiving(true);
    setError("");
    try {
      if (comp.archived) await unarchiveCompetition(user, comp.id);
      else await archiveCompetition(user, comp.id);
      await reload();
    } catch {
      setError(comp.archived ? t("admin.competition.unarchiveError") : t("admin.competition.archiveError"));
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteCompetition(user, comp.id, deleteWithLeagues);
      navigate("/admin/competitions");
    } catch {
      setError(t("admin.competitions.errors.failedToDelete"));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const warnings = advisoryWarnings(comp, t);
  const realGwCount = comp.gameweeks.filter((gw) => gw.number > 0).length;
  const completedGwCount = comp.gameweeks.filter((gw) => gw.number > 0 && gw.completedAt).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {error && <p style={errorStyle}>{error}</p>}

      {comp.status === "UPCOMING" ? (
        <div style={card}>
          <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.overview.preStart.title")}</h2>
          {warnings.length > 0 ? (
            <ul style={{ margin: "0 0 1rem", paddingLeft: "1.25rem", color: "var(--color-text-muted)", fontSize: "0.88rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              {warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          ) : (
            <p style={{ margin: "0 0 1rem", color: "var(--color-text-muted)", fontSize: "0.88rem" }}>{t("admin.overview.preStart.allSet")}</p>
          )}
          <button style={primaryBtn} onClick={() => setConfirmStart(true)} disabled={comp.clubs.length === 0}>
            {t("admin.competitions.actions.start")}
          </button>
        </div>
      ) : (
        <div style={card}>
          <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.overview.dashboard.title")}</h2>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--color-text)" }}>
            {t("admin.overview.dashboard.gwProgress", { completed: completedGwCount, total: realGwCount })}
          </p>
          {comp.supportedModes.length > 0 && (
            <p style={{ margin: "0.4rem 0 0", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              {comp.supportedModes.map((m) => competitionModeLabel(m)).join(", ")}
            </p>
          )}
        </div>
      )}

      <div style={card}>
        <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.overview.details.title")}</h2>

        <div style={{ marginBottom: "1rem" }}>
          <p style={{ ...sectionLabel, marginTop: 0 }}>{t("admin.overview.details.nameLabel")}</p>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            <input
              style={{ ...inputStyle, maxWidth: "320px", width: "100%" }}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveName(); }}
            />
            <button style={smallBtn} onClick={handleSaveName} disabled={savingName || !nameDraft.trim() || nameDraft.trim() === comp.name}>
              {savingName ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {comp.status === "COMPLETED" && (
            <button style={secondaryBtn} onClick={handleArchiveToggle} disabled={archiving}>
              {comp.archived ? t("admin.competition.unarchive") : t("admin.competition.archive")}
            </button>
          )}
          <button style={dangerBtn} onClick={() => setConfirmDelete(true)}>{t("admin.competitions.actions.delete")}</button>
        </div>
      </div>

      <div style={card}>
        <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.overview.access.title")}</h2>
        <p style={{ margin: "0 0 1rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
          {t("admin.overview.access.description")}
        </p>

        {addError && <p style={errorStyle}>{addError}</p>}

        {!grantsLoading && grants.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
            {grants.map((g) => (
              <div
                key={g.id}
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  padding: "0.5rem 0",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{g.user.displayName}</div>
                  <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{g.user.email}</div>
                </div>
                <button style={smallBtn} onClick={() => setRevokeTarget(g)}>
                  {t("admin.overview.access.revoke")}
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <input
            style={{ ...inputStyle, maxWidth: "320px", width: "100%" }}
            placeholder={t("admin.overview.access.addPlaceholder")}
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddGrant(); }}
          />
          <button style={smallBtn} onClick={handleAddGrant} disabled={addingGrant || !addEmail.trim()}>
            {addingGrant ? t("common.saving") : t("admin.overview.access.addBtn")}
          </button>
        </div>
        <p style={{ margin: "0.6rem 0 0", fontSize: "0.78rem", color: "var(--color-text-dim)" }}>
          {t("admin.overview.access.staleness")}
        </p>
      </div>

      {revokeTarget && (
        <ConfirmModal
          title={t("admin.overview.access.revokeConfirm.title")}
          message={t("admin.overview.access.revokeConfirm.message", { name: revokeTarget.user.displayName })}
          confirmLabel={t("admin.overview.access.revoke")}
          confirming={revoking}
          onConfirm={handleRevokeGrant}
          onCancel={() => setRevokeTarget(null)}
        />
      )}

      {confirmStart && (
        <ConfirmModal
          title={t("admin.overview.startConfirm.title")}
          message={t("admin.overview.startConfirm.message")}
          confirmLabel={t("admin.competitions.actions.start")}
          danger={false}
          confirming={starting}
          onConfirm={handleStart}
          onCancel={() => setConfirmStart(false)}
        />
      )}

      {confirmDelete && (
        <ConfirmDestructive
          title={t("admin.competitions.deleteModal.title")}
          message={t("admin.competitions.deleteModal.message", { name: comp.name })}
          confirmLabel={t("admin.competitions.deleteModal.confirm")}
          confirming={deleting}
          irreversible
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          impact={
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input type="checkbox" checked={deleteWithLeagues} onChange={(e) => setDeleteWithLeagues(e.target.checked)} />
              {t("admin.competitions.deleteModal.deleteLeaguesLabel")}
            </label>
          }
        />
      )}
    </div>
  );
}
